export const THRESHOLD_RUNGS = [
  {
    order: 1,
    reached: 'FIRST_THRESHOLD',
    near: 'NEAR_FIRST_THRESHOLD',
    label: 'First Threshold',
    tag: 'noticeDark',
    nearTag: 'notice',
  },
  {
    order: 2,
    reached: 'SECOND_THRESHOLD',
    near: 'NEAR_SECOND_THRESHOLD',
    label: 'Second Threshold',
    tag: 'attentionDark',
    nearTag: 'attention',
  },
  {
    order: 3,
    reached: 'THIRD_THRESHOLD',
    near: 'NEAR_THIRD_THRESHOLD',
    label: 'Third Threshold',
    tag: 'errorDark',
    nearTag: 'error',
  },
  {
    order: 4,
    reached: 'FOURTH_THRESHOLD',
    near: 'NEAR_FOURTH_THRESHOLD',
    label: 'Fourth Threshold',
    tag: 'errorDark',
    nearTag: 'errorDark',
  },
  {
    order: 5,
    reached: 'FIFTH_THRESHOLD',
    near: 'NEAR_FIFTH_THRESHOLD',
    label: 'Fifth Threshold',
    tag: 'errorDark',
    nearTag: 'errorDark',
  },
] as const;

export const ABOVE_THRESHOLD_STATUS_LABELS: string[] = THRESHOLD_RUNGS.map((rung) => rung.label);

export type ViolationPointsThresholdReached = 'SAFE' | (typeof THRESHOLD_RUNGS)[number]['reached'];
export type ViolationPointsThresholdStatus = ViolationPointsThresholdReached | (typeof THRESHOLD_RUNGS)[number]['near'];

export type ViolationPointsThresholdTagStatus =
  | 'success'
  | 'notice'
  | 'noticeDark'
  | 'attention'
  | 'attentionDark'
  | 'error'
  | 'errorDark'
  | 'neutral';

export type ViolationPointsThresholdRule = {
  id: number;
  pointsThreshold: number;
  stakeholdersToNotify: string[];
  escalationType: string;
  displayOrder: number;
  nearBandFraction: number;
};

const findThresholdRung = (status: string | null) =>
  THRESHOLD_RUNGS.find((rung) => rung.reached === status || rung.near === status);

export const isReachedThresholdStatus = (status: string | null): boolean =>
  status === 'SAFE' || THRESHOLD_RUNGS.some((rung) => rung.reached === status);

// A near status past the first rung implies an earlier rung was already reached.
export const isAboveThresholdStatus = (status: ViolationPointsThresholdStatus): boolean =>
  THRESHOLD_RUNGS.some((rung) => rung.reached === status) ||
  THRESHOLD_RUNGS.slice(1).some((rung) => rung.near === status);

export const getViolationPointsThresholdStatusMeta = (
  status: ViolationPointsThresholdStatus | null
): { label: string; status: ViolationPointsThresholdTagStatus } | null => {
  if (!status) return null;
  if (status === 'SAFE') return { label: 'Safe', status: 'success' };

  const rung = findThresholdRung(status);
  if (!rung) return { label: status, status: 'neutral' };

  const isNear = rung.near === status;
  return {
    label: isNear ? `Near ${rung.label}` : rung.label,
    status: isNear ? rung.nearTag : rung.tag,
  };
};

export const getViolationPointsThresholdStatusLabel = (
  status: ViolationPointsThresholdStatus | string | null
): string => getViolationPointsThresholdStatusMeta(status as ViolationPointsThresholdStatus)?.label ?? '';

const NEAR_ABOVE_THRESHOLD_LABELS: string[] = THRESHOLD_RUNGS.slice(1).map((rung) =>
  getViolationPointsThresholdStatusLabel(rung.near)
);

// Single source of truth for above-threshold table filtering, so filtered rows match the count.
export const ABOVE_THRESHOLD_FILTER_LABELS: string[] = [
  ...ABOVE_THRESHOLD_STATUS_LABELS,
  ...NEAR_ABOVE_THRESHOLD_LABELS,
];

export const isAboveThresholdFilterValue = (value: string | null): boolean => {
  if (!value) return false;
  return (
    isAboveThresholdStatus(value as ViolationPointsThresholdStatus) || ABOVE_THRESHOLD_FILTER_LABELS.includes(value)
  );
};

export const getThresholdReachedFilterLabel = (value: ViolationPointsThresholdReached | string | null): string => {
  if (!value) return '';
  if (value === 'SAFE') return 'Dropped below thresholds';
  return getViolationPointsThresholdStatusLabel(value);
};

const STAKEHOLDER_LABELS: Record<string, string> = {
  EMPLOYEE: 'Employee',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
};

export const getThresholdNotifyText = (stakeholdersToNotify: string[]): string => {
  const names = stakeholdersToNotify.map((stakeholder) => STAKEHOLDER_LABELS[stakeholder] ?? stakeholder);

  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} notified`;

  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]} notified`;
};

export const getViolationPointsThresholdDisplayOrder = (
  status: ViolationPointsThresholdReached | ViolationPointsThresholdStatus | null
): number | null => {
  if (status === 'SAFE') return 0;
  return THRESHOLD_RUNGS.find((rung) => rung.reached === status)?.order ?? null;
};

export const getThresholdOrdinalLabel = (displayOrder: number): string => {
  if (displayOrder === 1) return '1st';
  if (displayOrder === 2) return '2nd';
  if (displayOrder === 3) return '3rd';
  return `${displayOrder}th`;
};

type ThresholdSortable = {
  pointsThreshold?: number | null;
  displayOrder?: number | null;
};

const hasSortablePoints = (value: number | null | undefined): value is number => value != null && !Number.isNaN(value);

/** Backend order: points ascending, then displayOrder. Empty point values go last. */
export const sortThresholdRulesByPoints = <T extends ThresholdSortable>(rules: T[]): T[] =>
  [...rules].sort((a, b) => {
    const aHasPoints = hasSortablePoints(a.pointsThreshold);
    const bHasPoints = hasSortablePoints(b.pointsThreshold);

    if (aHasPoints && bHasPoints && a.pointsThreshold !== b.pointsThreshold) {
      return a.pointsThreshold - b.pointsThreshold;
    }
    if (aHasPoints !== bHasPoints) {
      return aHasPoints ? -1 : 1;
    }

    return (a.displayOrder ?? Number.POSITIVE_INFINITY) - (b.displayOrder ?? Number.POSITIVE_INFINITY);
  });

/** After a points-ascending sort, position is the ladder ordinal (1 = first threshold). */
export const getThresholdLadderOrdinal = (index: number): number => index + 1;

export type ViolationPointsThresholdReachedNote = {
  label: string;
  status: ViolationPointsThresholdTagStatus;
};

export const getThresholdReachedNote = (
  value: ViolationPointsThresholdReached | null
): ViolationPointsThresholdReachedNote | null => {
  if (!value || value === 'SAFE') return null;

  const rung = THRESHOLD_RUNGS.find((item) => item.reached === value);
  if (!rung) return null;

  return {
    label: rung.label,
    status: rung.tag,
  };
};

const LADDER_FILL = {
  safe: '#DBF2D3',
  warning: '#F6E6B2',
  danger: '#FFE1D9',
  empty: '#F2F2F2',
};

export const getLadderFillColor = (thresholdStatus: ViolationPointsThresholdStatus | null): string => {
  if (!thresholdStatus || thresholdStatus === 'SAFE') return LADDER_FILL.safe;

  const rung = findThresholdRung(thresholdStatus);
  if (!rung) return LADDER_FILL.empty;

  return rung.order <= 2 ? LADDER_FILL.warning : LADDER_FILL.danger;
};
